import React, { useState } from 'react';

import LargeCard from '../../main/large-card';
import useTranslate from '../../useTranslate';
import { Button, Col, FormControl, Row } from 'react-bootstrap';
import ValidatorFormGroup from './../../main/validator-form-group';
import { connect } from 'react-redux';
import { RootState } from '../../../store';
import { LocationCostIncreaseSuggestionDto } from '../../../store/locationsSlice';
import {
    LocationCostsDto,
    Schedule,
    SuggestedCostIncreaseAction,
    UseSuggestedCostIncrease,
} from '../../../store/locationCostsSlice';
import { RequestStatus } from '../../../store/network/managedRequest';
import { LANGS } from '../../../util/enums';
import ScheduleTimeSelect from './schedule-time-select';
import { PbRequest } from '../../../store/pbRequest';

interface Props {
    locationCosts: LocationCostsDto;
    suggestion: LocationCostIncreaseSuggestionDto;
    goToPricingScreen: () => void;

    requestStatus: RequestStatus;
    UseSuggestedCostIncrease: (
        locationId: number,
        action: SuggestedCostIncreaseAction,
        schedule: Schedule
    ) => void;
}

function SuggestedCostIncreaseCard(props: Readonly<Props>) {
    const strings = useTranslate('SuggestedPriceIncreaseCard');
    const [selectedSchedule, setSelectedSchedule] = useState<Schedule>(
        Schedule.IN_SEVEN_DAYS
    );

    const location = props.locationCosts;
    const currencyType = location.currency_code || 'EUR';
    const currencySymbol = strings._utils.getCurrencySymbol(currencyType);

    const waiting = RequestStatus.PENDING === props.requestStatus;
    const hasError = RequestStatus.FAILURE === props.requestStatus;

    const suggestion = props.suggestion;

    if (!suggestion) {
        return null;
    }

    return (
        <LargeCard
            type={'card-edit'}
            title={strings.title}
            description={getReasonText()}
        >
            <Row>
                <Col xs={12} className={'mb-2 mt-2'}>
                    <h3>{strings.currentPrices}</h3>
                </Col>
                <Col xs={6}>
                    {`${strings.oldPriceWash}: ${strings._utils.formatPrice({
                        amount: location.price_normal_washing_machine,
                        currency: currencyType,
                    })}`}
                </Col>
                <Col xs={6}>
                    {`${strings.oldPriceDry}: ${strings._utils.formatPrice({
                        amount: location.price_normal_dryer,
                        currency: currencyType,
                    })}`}
                </Col>
            </Row>
            <Row className={'mt-2 mb-2'}>
                <Col xs={12}>
                    <h3>{strings.weSuggest}</h3>
                </Col>
            </Row>
            <Row>
                <Col xs={6}>
                    <ValidatorFormGroup
                        label={strings._utils.sprintf(
                            strings.priceWash,
                            currencySymbol
                        )}
                    >
                        <FormControl
                            type="text"
                            className="disabled"
                            value={strings._utils.formatNumber(
                                suggestion.cost_washer,
                                2
                            )}
                            required
                            readOnly
                        />
                    </ValidatorFormGroup>
                </Col>
                <Col xs={6}>
                    <ValidatorFormGroup
                        label={strings._utils.sprintf(
                            strings.priceDry,
                            currencySymbol
                        )}
                    >
                        <FormControl
                            type="text"
                            className="disabled"
                            value={strings._utils.formatNumber(
                                suggestion.cost_dryer,
                                2
                            )}
                            required
                            readOnly
                        />
                    </ValidatorFormGroup>
                </Col>
            </Row>
            <Row>
                <Col xs={12}>{renderSchedule()}</Col>
            </Row>
            {hasError && (
                <Row>
                    <Col xs={12}>
                        <div className="alert alert-danger">
                            {strings.genericError}
                        </div>
                    </Col>
                </Row>
            )}
            <Row>
                <Button
                    className={'btn btn-primary mr-2'}
                    type={'button'}
                    onClick={handleAccept}
                    disabled={waiting}
                >
                    {strings.btnAccept}
                </Button>

                <Button
                    className={'btn btn-default mr-2'}
                    type={'button'}
                    onClick={handleReject}
                    disabled={waiting}
                >
                    {strings.btnReject}
                </Button>

                <Button
                    className={'btn btn-default'}
                    type={'button'}
                    onClick={props.goToPricingScreen}
                    disabled={waiting}
                >
                    {strings.btnGoToPricingScreen}
                </Button>
            </Row>
        </LargeCard>
    );

    function renderSchedule() {
        const hint =
            selectedSchedule === Schedule.IMMEDIATELY
                ? strings.hintImmediately
                : strings.hintDelayed;

        return (
            <ScheduleTimeSelect
                label={strings.schedule}
                hint={hint}
                value={selectedSchedule}
                onChange={handleScheduleChange}
            />
        );

        function handleScheduleChange(
            event: React.ChangeEvent<HTMLSelectElement>
        ) {
            const target: HTMLSelectElement = event.target;
            setSelectedSchedule(target.value as Schedule);
        }
    }

    function getReasonText() {
        if (strings._currentLanguage === LANGS.ger) {
            return suggestion.reason_de;
        } else {
            return suggestion.reason_en;
        }
    }

    function handleAccept() {
        props.UseSuggestedCostIncrease(
            location.laundry_room_id,
            SuggestedCostIncreaseAction.USE,
            selectedSchedule
        );
    }

    function handleReject() {
        props.UseSuggestedCostIncrease(
            location.laundry_room_id,
            SuggestedCostIncreaseAction.IGNORE,
            selectedSchedule
        );
    }
}

export default connect(
    (state: RootState) => ({
        requestStatus:
            state.pbRequest[PbRequest.POST_USE_SUGGESTED_COST_INCREASE].status,
    }),
    { UseSuggestedCostIncrease }
)(SuggestedCostIncreaseCard);
